import React, { useRef, useEffect } from 'react';
import { Input } from './ui/input';

// Track when a Google Places pac-container was last visible.
// dialog.jsx reads window.__pacLastVisibleAt to avoid closing the dialog
// when a user clicks a suggestion (Radix sees it as "outside" click).
if (typeof window !== 'undefined' && !window.__pacLastVisibleAt) {
  window.__pacLastVisibleAt = 0;

  // Use a MutationObserver on body to detect pac-container visibility changes.
  const observer = new MutationObserver(() => {
    const pacs = document.querySelectorAll('.pac-container');
    for (const p of pacs) {
      if (p.style.display !== 'none' && p.offsetHeight > 0) {
        window.__pacLastVisibleAt = Date.now();
        return;
      }
    }
  });
  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['style'] });
    });
  }
}

const AddressAutocomplete = ({
  value,
  onChange,
  onPlaceSelected,
  placeholder = '123 Main St',
  googleApiKey,
  disabled = false,
  ...inputProps
}) => {
  const inputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const callbacksRef = useRef({ onPlaceSelected, onChange });

  useEffect(() => {
    callbacksRef.current = { onPlaceSelected, onChange };
  }, [onPlaceSelected, onChange]);

  useEffect(() => {
    if (!googleApiKey) return;

    const loadAndInit = () => {
      if (!inputRef.current || autocompleteRef.current) return;

      const autocomplete = new window.google.maps.places.Autocomplete(
        inputRef.current,
        { types: ['address'] }
      );

      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (!place.address_components) return;

        let street_number = '';
        let route = '';
        let city = '';
        let state = '';
        let zip = '';

        place.address_components.forEach((component) => {
          const type = component.types[0];
          if (type === 'street_number') street_number = component.long_name;
          if (type === 'route') route = component.short_name;
          if (type === 'locality') city = component.long_name;
          if (type === 'administrative_area_level_1') state = component.short_name;
          if (type === 'postal_code') zip = component.long_name;
        });

        const address = [street_number, route].filter(Boolean).join(' ');
        if (callbacksRef.current.onPlaceSelected) {
          callbacksRef.current.onPlaceSelected({ address, city, state, zip });
        }
      });

      autocompleteRef.current = autocomplete;
    };

    if (window.google?.maps?.places) {
      loadAndInit();
      return;
    }

    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      const interval = setInterval(() => {
        if (window.google?.maps?.places) {
          clearInterval(interval);
          loadAndInit();
        }
      }, 200);
      return () => clearInterval(interval);
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${googleApiKey}&libraries=places`;
    script.async = true;
    script.onload = () => setTimeout(loadAndInit, 100);
    document.head.appendChild(script);
  }, [googleApiKey]);

  useEffect(() => {
    return () => { autocompleteRef.current = null; };
  }, []);

  return (
    <Input
      ref={inputRef}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      {...inputProps}
    />
  );
};

export default AddressAutocomplete;
